import React, { useCallback } from "react";
import CustomAutocomplete from "../../../components/CustomForm/CustomAutocomplete";
import CheckRender from "../../../components/Common/CheckRender";
import CustomAlert from "../../../components/Common/CustomAlert";
import { MODALIDADES_PSS } from "../utils/HospitalizacionConstant";
import useCoberturasMedicos from "../hooks/useCoberturasMedicos";
import {
  formatCoberturaLabel,
  formatMedicoLabel,
} from "../utils/CoberturasMedicosUtils";
import {
  SectionContainer,
  CoberturasList,
  CoberturaItem,
  CoberturaInputsRowPaquete,
  CoberturaInputsRowArs,
  InputGroupCobertura,
  InputGroupCoberturaPaquete,
  InputGroupMedico,
  InputGroupMedicoPaquete,
  InputGroupEspecialidad,
  ModalidadRow,
  ModalidadLabel,
  ModalidadText,
  InputFieldContainer,
  InputLabel,
  SimpleInput,
} from "../styles/CoberturasMedicosSectionStyles";

const CoberturasMedicosSection = ({
  coberturasMedicos,
  onActualizarCobertura,
  onActualizarMedico,
  onActualizarModalidadPss,
  onActualizarNombreMedico,
  onActualizarEspecialidadMedico,
  onReiniciarCampos,
  obtenerMedicosDisponibles,
  getProcedimientosPssData,
  getProcedimientosPssLoading,
  getProcedimientosPssError,
  getMedicosData,
  getMedicosLoading,
  getMedicosError,
  manejarBusquedaProcedimiento,
  manejarBusquedaMedico,
  consultarTipoPssNoMedicoData,
  obtenerEspecialidadesMedicasData,
  obtenerEspecialidadesMedicasLoading,
  obtenerEspecialidadesMedicasError,
}) => {
  const {
    especialidadesOptions,
    coberturasOptions,
    medicosOptions,
    showCoberturaNoContratadaAlert,
    showPrestadorSinInformacionAlert,
    handleActualizarModalidadPss,
    handleCoberturaChange,
    handleCoberturaSelect,
    handleMedicoChange,
    handleMedicoSelect,
    handleEspecialidadChange,
    handleEspecialidadSelect,
    getCoberturaAutocompleteRef,
    getMedicoAutocompleteRef,
    getEspecialidadAutocompleteRef,
    handleCerrarCoberturaNoContratadaAlert,
    setShowPrestadorSinInformacionAlert,
  } = useCoberturasMedicos({
    coberturasMedicos,
    onActualizarCobertura,
    onActualizarMedico,
    onActualizarModalidadPss,
    onActualizarEspecialidadMedico,
    onReiniciarCampos,
    obtenerMedicosDisponibles,
    getProcedimientosPssData,
    getProcedimientosPssLoading,
    getProcedimientosPssError,
    getMedicosData,
    getMedicosLoading,
    getMedicosError,
    manejarBusquedaProcedimiento,
    manejarBusquedaMedico,
    consultarTipoPssNoMedicoData,
    obtenerEspecialidadesMedicasData,
    obtenerEspecialidadesMedicasLoading,
    obtenerEspecialidadesMedicasError,
  });

  const renderCoberturaAutocomplete = useCallback(
    (item) => (
      <CustomAutocomplete
        ref={getCoberturaAutocompleteRef(item.id)}
        label="Seleccionar Cobertura"
        placeholder="Buscar por código o nombre..."
        value={formatCoberturaLabel(item.cobertura)}
        onChange={(value) => handleCoberturaChange(item.id, value)}
        onSelect={(option) => handleCoberturaSelect(item.id, option)}
        options={coberturasOptions}
        displayKey="label"
        valueKey="value"
        height="32px"
        fontSize="13px"
        required
        isLoading={getProcedimientosPssLoading}
        loadingText="Buscando coberturas..."
      />
    ),
    [
      coberturasOptions,
      getProcedimientosPssLoading,
      handleCoberturaChange,
      handleCoberturaSelect,
      getCoberturaAutocompleteRef,
    ]
  );

  const renderMedicoAutocomplete = useCallback(
    (item) => {
      const tieneCobertura = Boolean(item.cobertura);

      return (
        <CustomAutocomplete
          ref={getMedicoAutocompleteRef(item.id)}
          label="Seleccionar Médico"
          placeholder="Buscar por código o nombre..."
          value={formatMedicoLabel(item.medico)}
          onChange={(value) => handleMedicoChange(item.id, value)}
          onSelect={(option) => handleMedicoSelect(item.id, option)}
          options={medicosOptions}
          displayKey="label"
          valueKey="value"
          height="32px"
          fontSize="13px"
          required
          isLoading={getMedicosLoading}
          loadingText="Buscando médicos..."
          disabled={!tieneCobertura}
        />
      );
    },
    [
      medicosOptions,
      getMedicosLoading,
      handleMedicoChange,
      handleMedicoSelect,
      getMedicoAutocompleteRef,
    ]
  );

  const renderModalidadRadios = useCallback(
    (item) => {
      const tieneCobertura = Boolean(item.cobertura);
      return (
        <ModalidadRow>
          <ModalidadText>Modalidad PSS:</ModalidadText>
          <ModalidadLabel>
            <input
              type="radio"
              name={`modalidadPss_${item.id}`}
              value={MODALIDADES_PSS.ARS}
              checked={item.modalidadPss === MODALIDADES_PSS.ARS}
              onChange={(e) =>
                handleActualizarModalidadPss(item.id, e.target.value)
              }
              disabled={tieneCobertura}
            />
            MÉDICO ARS
          </ModalidadLabel>
          <ModalidadLabel>
            <input
              type="radio"
              name={`modalidadPss_${item.id}`}
              value={MODALIDADES_PSS.PAQUETE}
              checked={item.modalidadPss === MODALIDADES_PSS.PAQUETE}
              onChange={(e) =>
                handleActualizarModalidadPss(item.id, e.target.value)
              }
              disabled={tieneCobertura}
            />
            MÉDICO PAQUETE
          </ModalidadLabel>
        </ModalidadRow>
      );
    },
    [handleActualizarModalidadPss]
  );

  const renderCoberturaItem = useCallback(
    (item) => {
      const tipoReclamante =
        consultarTipoPssNoMedicoData?.tipoReclamante || MODALIDADES_PSS.ARS;
      const isArsMode =
        item.modalidadPss === MODALIDADES_PSS.ARS &&
        (tipoReclamante === MODALIDADES_PSS.HIBRIDO ||
          tipoReclamante === MODALIDADES_PSS.ARS);
      const isPaqueteMode =
        item.modalidadPss === MODALIDADES_PSS.PAQUETE &&
        (tipoReclamante === MODALIDADES_PSS.HIBRIDO ||
          tipoReclamante === MODALIDADES_PSS.PAQUETE);
      const isHibrido = tipoReclamante === MODALIDADES_PSS.HIBRIDO;

      return (
        <CoberturaItem key={item.id}>
          <CheckRender allowed={isArsMode}>
            <CoberturaInputsRowArs>
              <InputGroupCobertura>
                {renderCoberturaAutocomplete(item)}
              </InputGroupCobertura>
              <InputGroupMedico>
                {renderMedicoAutocomplete(item)}
              </InputGroupMedico>
            </CoberturaInputsRowArs>
          </CheckRender>

          <CheckRender allowed={isPaqueteMode}>
            <CoberturaInputsRowPaquete>
              <InputGroupCoberturaPaquete>
                {renderCoberturaAutocomplete(item)}
              </InputGroupCoberturaPaquete>
              <InputGroupMedicoPaquete>
                <InputFieldContainer>
                  <InputLabel>Nombre del Médico <span style={{ color: "red" }}>*</span></InputLabel>
                  <SimpleInput
                    type="text"
                    placeholder="Ingrese el nombre del médico..."
                    value={item.medico?.nombre || ""}
                    onChange={(e) =>
                      onActualizarNombreMedico(item.id, e.target.value)
                    }
                    required
                    disabled={!item.cobertura}
                  />
                </InputFieldContainer>
              </InputGroupMedicoPaquete>
              <InputGroupEspecialidad>
                <CustomAutocomplete
                  ref={getEspecialidadAutocompleteRef(item.id)}
                  label="Especialidad"
                  placeholder="Seleccionar especialidad..."
                  value={item.medico?.especialidad || ""}
                  onChange={(value) => handleEspecialidadChange(item.id, value)}
                  onSelect={(option) =>
                    handleEspecialidadSelect(item.id, option)
                  }
                  options={especialidadesOptions}
                  displayKey="label"
                  valueKey="value"
                  height="32px"
                  fontSize="13px"
                  required
                  disabled={!item.cobertura}
                  isLoading={obtenerEspecialidadesMedicasLoading}
                  loadingText="Cargando especialidades..."
                />
              </InputGroupEspecialidad>
            </CoberturaInputsRowPaquete>
          </CheckRender>

          <CheckRender allowed={isHibrido}>
            {renderModalidadRadios(item)}
          </CheckRender>
        </CoberturaItem>
      );
    },
    [
      renderCoberturaAutocomplete,
      renderMedicoAutocomplete,
      renderModalidadRadios,
      onActualizarNombreMedico,
      handleEspecialidadChange,
      handleEspecialidadSelect,
      especialidadesOptions,
      getEspecialidadAutocompleteRef,
      consultarTipoPssNoMedicoData?.tipoReclamante,
      obtenerEspecialidadesMedicasLoading,
    ]
  );

  return (
    <SectionContainer>
      <CoberturasList>
        {coberturasMedicos.map(renderCoberturaItem)}
      </CoberturasList>

      <CheckRender allowed={showCoberturaNoContratadaAlert}>
        <CustomAlert
          type="warning"
          title="Cobertura no contratada"
          message="La cobertura seleccionada no se muestra como contratada desde tu perfil de prestador. Por favor comuníquese con nosotros al 809-476-3535 para brindarle asistencia."
          onConfirm={handleCerrarCoberturaNoContratadaAlert}
          confirmText="Entendido"
          maxWidth="500px"
        />
      </CheckRender>

      <CheckRender allowed={showPrestadorSinInformacionAlert}>
        <CustomAlert
          type="warning"
          title="Información no disponible"
          message="No se encontró información de este prestador."
          onConfirm={() => setShowPrestadorSinInformacionAlert(false)}
          confirmText="Entendido"
          maxWidth="500px"
        />
      </CheckRender>
    </SectionContainer>
  );
};

export default CoberturasMedicosSection;
